import type { AppConfig } from '../types/config.types';

/** Backend envía este literal cuando no hay cotización (AssetResponse). */
export const SIN_COTIZAR = 'Sin cotizar';

/**
 * Misma etiqueta que el combo Activo en originación paso 3: línea sin activo cotizado (solo moneda/monto).
 * Usada también en monto a otorgar y vistas de solo lectura (comité, etc.).
 */
export const ACTIVO_SIN_COTIZACION_LABEL = 'Moneda';

export const USD_HISTORIC_LOCK_BANNER_TEXT =
  'Esta carpeta contiene productos en USD. La edición no está disponible porque USD está deshabilitado para este cliente.';

/** `true` si el activo tiene valor de venta cotizado (número válido). */
export function hasQuotatedSaleValue(saleValue: unknown): boolean {
  if (saleValue === SIN_COTIZAR) return false;
  return typeof saleValue === 'number' && !Number.isNaN(saleValue);
}

export function assetHasQuotation(asset: { saleValue?: unknown }): boolean {
  return hasQuotatedSaleValue(asset?.saleValue);
}

/**
 * Respuesta paginada del BFF (camelCase). Indica si hay al menos una cotización registrada.
 */
export function quotationPageHasEntries(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  const total = d.totalElements;
  if (typeof total === 'number' && total > 0) return true;
  const content = d.content;
  return Array.isArray(content) && content.length > 0;
}

/** FX asset code — usable as product-line currency when quotations-first allows it; never as Activo. */
export const USD_ASSET_CODE = 'USD';

/** Non-product assets excluded from Activo dropdowns (USD = FX; INF = inflation index). */
const ACTIVO_EXCLUDED_ASSET_CODES = new Set([USD_ASSET_CODE, 'INF']);

type ProductWithCurrency = {
  amount?: { currency_code?: string };
  currency_code?: string;
};

/**
 * Tenant local currency from login config. Currency SoT — not `isUruguayEnabled`.
 * Unknown / missing → ARS (safe default for legacy tenants).
 */
export function resolveLocalCurrencyCode(
  config: Pick<AppConfig, 'localCurrencyCode'>
): 'ARS' | 'UYU' {
  return config.localCurrencyCode === 'UYU' ? 'UYU' : 'ARS';
}

/** True when the catalog code must not appear as selectable Activo (USD FX / INF). */
export function isActivoExcludedAssetCode(code: string | undefined | null): boolean {
  if (!code) return false;
  return ACTIVO_EXCLUDED_ASSET_CODES.has(code);
}

/**
 * Activo dropdown catalog: drop FX/index assets. USD remains the FX quotation asset only
 * (no UYU asset needed — local denomination is `localCurrencyCode`).
 */
export function filterActivoSelectableAssets<T extends { code?: string }>(assets: T[]): T[] {
  return assets.filter((a) => !isActivoExcludedAssetCode(a.code));
}

/**
 * Quotations-first: si isQuotationsEnabled, USD depende de cotización + asset seleccionable;
 * si no, rige flagUsdCurrencyEnabled (default true).
 */
export function resolveUsdAvailable(
  config: Pick<AppConfig, 'isQuotationsEnabled' | 'flagUsdCurrencyEnabled'>,
  usdQuotationAvailable: boolean | null = null
): boolean {
  if (config.isQuotationsEnabled) {
    return usdQuotationAvailable === true;
  }
  return config.flagUsdCurrencyEnabled ?? true;
}

/** Detecta líneas existentes en USD (originación, comité o monto a otorgar). */
export function hasHistoricUsdProducts(products: ProductWithCurrency[]): boolean {
  return products.some((p) => {
    const code = p.amount?.currency_code ?? p.currency_code;
    return code === USD_ASSET_CODE;
  });
}

/** Lock de solo lectura cuando hay USD histórico y USD no está disponible para edición. */
export function isUsdHistoricLocked(usdAvailable: boolean, hasHistoricUsd: boolean): boolean {
  return !usdAvailable && hasHistoricUsd;
}

/**
 * Paso 3 Solicitud — dropdown Moneda: USD solo si hay cotización USD registrada
 * y el asset USD es seleccionable (viene en GET /applications/quotations, `findBySelectableTrue`).
 * Así se puede mantener cotización USD en BD (p. ej. comité) sin ofrecer USD como moneda si `selectable = false`.
 */
export function isUsdSelectableForOriginationCurrencyDropdown(
  usdQuotationPage: unknown,
  selectableAssetsFromCatalog: Array<{ code?: string }>
): boolean {
  return (
    quotationPageHasEntries(usdQuotationPage) &&
    selectableAssetsFromCatalog.some((a) => a.code === USD_ASSET_CODE)
  );
}

/** Ítems del dropdown Moneda (paso 3 Solicitud): USD solo si usdAvailable es true. */
export function buildOriginationCurrencyDropdownItems(
  arsCode: string,
  usdCode: string,
  usdAvailable: boolean | null
): Array<{ label: string }> {
  return [
    { label: arsCode },
    ...(usdAvailable === true ? [{ label: usdCode }] : []),
  ];
}
